/* BLANKD product page.
 *
 * Colour picks the variant/photo. Delivery speed is a real choice carried onto the quote
 * line. The "Get a Quote" panel lets a customer order several sizes at once — a real bulk
 * apparel order, not a single size × qty pair — and the bulk-pricing tier is driven by the
 * total across every size, same numbers the studio itself would quote.
 *
 * There is deliberately NO add-to-cart here: buying an undecorated blank was removed, so the
 * only route to checkout is through the studio. The quote list is the other exit.
 */
(() => {
  const root = document.querySelector("[data-bd-product]");
  if (!root) return;

  const $ = (s, r = root) => r.querySelector(s);
  const $$ = (s, r = root) => [...r.querySelectorAll(s)];
  const read = (s) => { try { return JSON.parse($(s).textContent); } catch { return null; } };
  const on = (els, el) => els.forEach((x) => x.classList.toggle("is-on", x === el));

  const variants = read("[data-bd-variants]") || [];
  const tiers = (read("[data-bd-bulk]") || []).slice().sort((a, b) => a.min - b.min);
  const QUOTE_URL = root.dataset.quoteUrl || "/pages/request-a-quote";
  const PRODUCT = { title: root.dataset.productTitle, handle: root.dataset.productHandle };

  const fork = $(".bd-fork");
  const basePriceCents = fork ? +fork.dataset.basePriceCents || 0 : 0;
  const money = (cents) => {
    try { return new Intl.NumberFormat("en-ZA", { style: "currency", currency: Shopify?.currency?.active || "ZAR" }).format(cents / 100); }
    catch { return `R${(cents / 100).toFixed(2)}`; }
  };

  const state = { color: null, delivery: "rush", shot: 0, qty: {} };
  const DELIVERY_DEFAULTS = { free: 14, standard: 7, rushMin: 3, rushMax: 5 };
  const DELIVERY_LABEL = {
    free: "FREE Delivery",
    standard: "Standard Delivery",
    rush: "Rush Delivery",
    // legacy keys from older quote lines / drafts
    fast: "Rush Delivery",
  };

  /* Working-day ETA helper (Mon–Fri, SA public holidays 2026–2027).
   * Clock starts the calendar day after the later of artwork approval and payment.
   * Until those clear, provisional from "now". Mixed-cart consumers can take max days
   * per tier across line items using the same helpers. */
  const SA_HOLIDAYS = new Set([
    // 2026
    "2026-01-01", "2026-03-21", "2026-04-03", "2026-04-06", "2026-04-27",
    "2026-05-01", "2026-06-16", "2026-08-09", "2026-09-24", "2026-12-16",
    "2026-12-25", "2026-12-26",
    // 2027
    "2027-01-01", "2027-03-21", "2027-03-22", // Human Rights Day observed Mon if Sun
    "2027-03-26", "2027-03-29", "2027-04-27", "2027-05-01", "2027-06-16",
    "2027-08-09", "2027-09-24", "2027-12-16", "2027-12-25", "2027-12-26",
    "2027-12-27", // Christmas observed Mon if Sat/Sun cascade — keep common observed dates
  ]);
  const pad2 = (n) => String(n).padStart(2, "0");
  const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const startOfLocalDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const addCalendarDays = (d, n) => {
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    x.setDate(x.getDate() + n);
    return x;
  };
  const isWorkingDay = (d) => {
    const day = d.getDay();
    if (day === 0 || day === 6) return false;
    return !SA_HOLIDAYS.has(ymd(d));
  };
  /** First calendar day of the clock: day after `from` (approval/payment/now). */
  const clockStart = (from = new Date()) => addCalendarDays(startOfLocalDay(from), 1);
  /** Delivery date after `n` working days from clock start (nth working day on/after start). */
  const addWorkingDays = (start, n) => {
    const need = Math.max(0, Math.floor(Number(n) || 0));
    if (need === 0) return startOfLocalDay(start);
    let d = startOfLocalDay(start);
    let counted = 0;
    // Cap iterations so a bad n cannot hang the page.
    for (let i = 0; i < need + 400; i++) {
      if (isWorkingDay(d)) {
        counted++;
        if (counted === need) return d;
      }
      d = addCalendarDays(d, 1);
    }
    return d;
  };
  const formatEst = (d) => {
    try {
      // "Fri 9 Oct" — punchy, still scannable.
      const parts = new Intl.DateTimeFormat("en-ZA", {
        weekday: "short", month: "short", day: "numeric",
      }).formatToParts(d);
      const get = (t) => (parts.find((p) => p.type === t) || {}).value || "";
      return `${get("weekday")} ${get("day")} ${get("month")}`;
    } catch {
      return d.toDateString();
    }
  };
  const readDeliveryConfig = () => {
    const raw = read("[data-bd-delivery]") || {};
    const dflt = { ...DELIVERY_DEFAULTS, ...(raw.defaults || {}) };
    let free = Number(raw.free?.days);
    let standard = Number(raw.standard?.days);
    let rushMin = Number(raw.rush?.daysMin);
    let rushMax = Number(raw.rush?.daysMax);
    if (!Number.isFinite(free) || free < 1) free = dflt.free;
    if (!Number.isFinite(standard) || standard < 1) standard = dflt.standard;
    if (!Number.isFinite(rushMin) || rushMin < 1) rushMin = dflt.rushMin;
    if (!Number.isFinite(rushMax) || rushMax < 1) rushMax = dflt.rushMax;
    // Validate: rush_max ≥ rush_min ≥ 1; standard > rush_max; free > standard.
    if (rushMax < rushMin) rushMax = rushMin;
    if (!(standard > rushMax && free > standard)) {
      free = dflt.free;
      standard = dflt.standard;
      rushMin = dflt.rushMin;
      rushMax = dflt.rushMax;
    }
    return {
      free: { days: free, label: raw.free?.label || DELIVERY_LABEL.free, enabled: raw.free?.enabled !== false },
      standard: { days: standard, label: raw.standard?.label || DELIVERY_LABEL.standard, enabled: raw.standard?.enabled !== false },
      rush: { daysMin: rushMin, daysMax: rushMax, label: raw.rush?.label || DELIVERY_LABEL.rush, enabled: raw.rush?.enabled !== false },
    };
  };
  const deliveryCfg = readDeliveryConfig();
  const paintDeliveryDates = (approvalOrPayment) => {
    const start = clockStart(approvalOrPayment || new Date());
    // Punchy: one line, the target date(s). "By Fri 9 Oct" / range "By Wed 23 – Mon 28 Sep".
    const setSub = (key, text) => {
      const el = root.querySelector(`[data-deliv-sub="${key}"]`);
      if (el) el.textContent = text;
    };
    if (deliveryCfg.free.enabled) {
      const d = addWorkingDays(start, deliveryCfg.free.days);
      setSub("free", `By ${formatEst(d)}`);
    }
    if (deliveryCfg.standard.enabled) {
      const d = addWorkingDays(start, deliveryCfg.standard.days);
      setSub("standard", `By ${formatEst(d)}`);
    }
    if (deliveryCfg.rush.enabled) {
      const a = addWorkingDays(start, deliveryCfg.rush.daysMin);
      const b = addWorkingDays(start, deliveryCfg.rush.daysMax);
      setSub(
        "rush",
        ymd(a) === ymd(b) ? `By ${formatEst(a)}` : `By ${formatEst(a)} – ${formatEst(b)}`
      );
    }
  };
  // Expose for cart/customizer: max days per tier across line items.
  window.BlankdDelivery = {
    defaults: DELIVERY_DEFAULTS,
    labels: DELIVERY_LABEL,
    isWorkingDay,
    clockStart,
    addWorkingDays,
    formatEst,
    config: deliveryCfg,
    maxDaysForCart(lines, tier) {
      const nums = (lines || []).map((l) => {
        if (tier === "rush") return Math.max(Number(l.rushDaysMax || l.days) || 0, Number(l.rushDaysMin) || 0);
        if (tier === "standard") return Number(l.standardDays || l.days) || 0;
        return Number(l.freeDays || l.days) || 0;
      }).filter((n) => n > 0);
      return nums.length ? Math.max(...nums) : null;
    },
  };

  const KEY = "blankd:quote:v1";
  // Guarded: private windows and blocked site data throw on access, and a customer hitting
  // that should still get a working page, just without a list.
  const loadList = () => { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; } };
  const saveList = (l) => { try { localStorage.setItem(KEY, JSON.stringify(l)); } catch {} };

  /* gallery */
  const shots = $$(".bd-view__img"), thumbs = $$(".bd-thumb");
  function showShot(i) {
    if (!shots.length) return;
    state.shot = (i + shots.length) % shots.length;
    on(shots, shots[state.shot]);
    on(thumbs, thumbs[state.shot]);
  }
  thumbs.forEach((t) => t.addEventListener("click", () => showShot(+t.dataset.shot)));
  $$("[data-nav]").forEach((b) => b.addEventListener("click", () => showShot(state.shot + +b.dataset.nav)));

  /* delivery speed — a real choice, carried onto the quote line item below. */
  const deliveryBtns = $$("[data-delivery]");
  function setDelivery(d) {
    // Map legacy "fast" → "rush" if an old template somehow remains cached.
    if (d === "fast") d = "rush";
    state.delivery = d;
    on(deliveryBtns, deliveryBtns.find((b) => b.dataset.delivery === d));
  }
  deliveryBtns.forEach((b) => b.addEventListener("click", () => setDelivery(b.dataset.delivery)));
  // Prefer Rush (FASTEST) when present; else first available card.
  const preferred = deliveryBtns.find((b) => b.dataset.delivery === "rush") || deliveryBtns[0];
  if (preferred) setDelivery(preferred.dataset.delivery);
  paintDeliveryDates();

  /* variants — colour drives the shown price and, where a size exists, which sizes are
     actually orderable in that colour. */
  const variantFor = (c, s) => variants.find((v) => (c == null || v.c === c) && (s == null || v.s === s));

  /* quote-panel size rows. Each row is keyed by its visible label; "One Size" is a sentinel
     for products with no size option at all — variantFor(colour, null) still resolves them
     to the single real variant. */
  const rows = $$("[data-size-row]");
  rows.forEach((r) => { state.qty[r.dataset.sizeRow] = 0; });

  function totalQty() {
    return Object.values(state.qty).reduce((a, n) => a + n, 0);
  }
  function tierFor(qty) {
    if (!tiers.length) return null;
    return tiers.reduce((best, t) => (qty >= t.min ? t : best), tiers[0]);
  }
  function nextTierUp(qty) {
    return tiers.find((t) => t.min > qty);
  }

  function paintQty() {
    rows.forEach((r) => {
      const label = r.dataset.sizeRow;
      const el = $(`[data-qty-for="${CSS.escape(label)}"]`, r);
      if (el) el.textContent = state.qty[label] || 0;
      const dec = $(`[data-qdec]`, r);
      if (dec) dec.disabled = !(state.qty[label] > 0);
    });

    const total = totalQty();
    const totalEl = $("[data-total-qty]");
    if (totalEl) totalEl.textContent = total;

    const t = tierFor(total);
    $$(".bd-tier").forEach((el) => el.classList.toggle("is-on", t && +el.dataset.tierMin === t.min));

    const unitCents = t && t.unitCents != null ? t.unitCents : basePriceCents;
    const priceEl = $("[data-total-price]");
    if (priceEl) priceEl.textContent = total > 0 ? money(unitCents * total) : "—";

    const nudge = $("[data-nudge]");
    if (nudge) {
      const next = nextTierUp(total);
      if (total > 0 && next && next.unitCents != null) {
        nudge.hidden = false;
        nudge.textContent = `Add ${next.min - total} more to drop to ${money(next.unitCents)}/piece`;
      } else {
        nudge.hidden = true;
      }
    }

    const addBtn = $("[data-quote-add]");
    if (addBtn) addBtn.disabled = total === 0;
  }

  function setQty(label, qty) {
    const row = rows.find((r) => r.dataset.sizeRow === label);
    if (!row) return;
    const v = row === rows[0] && rows.length === 1 && label === "One Size" && !$(".bd-szb")
      ? variantFor(state.color, null) : variantFor(state.color, label);
    const max = v && v.ok ? 9999 : 0; // sold-out sizes can't take quantity
    state.qty[label] = Math.max(0, Math.min(max, qty));
  }

  rows.forEach((r) => {
    const label = r.dataset.sizeRow;
    $("[data-qinc]", r)?.addEventListener("click", () => { setQty(label, (state.qty[label] || 0) + 1); paintQty(); });
    $("[data-qdec]", r)?.addEventListener("click", () => { setQty(label, (state.qty[label] || 0) - 1); paintQty(); });
  });

  /* quick fill: sets the total to N, spread evenly across every in-stock size (remainder to
     the first rows) — or straight onto the single row for a one-size product. */
  $$("[data-quickfill]").forEach((b) => b.addEventListener("click", () => {
    const n = +b.dataset.quickfill;
    const eligible = rows.filter((r) => {
      const label = r.dataset.sizeRow;
      const v = rows.length === 1 && label === "One Size" && !$(".bd-szb")
        ? variantFor(state.color, null) : variantFor(state.color, label);
      return !v || v.ok; // no variant record at all (one-size fallback) counts as eligible
    });
    if (!eligible.length) return;
    const base = Math.floor(n / eligible.length);
    const rem = n % eligible.length;
    eligible.forEach((r, i) => setQty(r.dataset.sizeRow, base + (i < rem ? 1 : 0)));
    rows.filter((r) => !eligible.includes(r)).forEach((r) => setQty(r.dataset.sizeRow, 0));
    paintQty();
  }));

  const qpanel = $("[data-qpanel]");
  $("[data-quote-toggle]")?.addEventListener("click", (e) => {
    const open = qpanel.classList.toggle("is-on");
    e.currentTarget.setAttribute("aria-expanded", String(open));
  });

  function sync() {
    // Re-validate every size row against the newly selected colour — a size that's sold out
    // in this colour gets its quantity cleared rather than silently kept.
    rows.forEach((r) => {
      const label = r.dataset.sizeRow;
      const v = rows.length === 1 && label === "One Size" && !$(".bd-szb")
        ? variantFor(state.color, null) : variantFor(state.color, label);
      if (v && !v.ok) state.qty[label] = 0;
    });

    const v = variantFor(state.color, null);
    // Ex-VAT and incl-VAT are pre-formatted server-side (blankd-product.liquid) so the JS never
    // reimplements Shopify's money formatting — it only swaps in the numbers for the selected colour.
    const priceExEl = $("[data-price-ex]");
    const priceInEl = $("[data-price-in]");
    if (v) {
      if (priceExEl) priceExEl.textContent = v.exPriceText;
      if (priceInEl) priceInEl.textContent = v.priceText;
    }

    const nameEl = $("[data-color-name]");
    if (nameEl) nameEl.textContent = state.color || "";

    const avail = $("[data-avail]");
    if (avail) {
      avail.innerHTML = rows.map((r) => {
        const label = r.dataset.sizeRow;
        const vv = variantFor(state.color, label);
        const txt = !vv ? "Not made" : vv.ok ? "In stock" : "Out of stock";
        return `<div class="bd-sz"><span class="bd-sz__k">${label}</span><span class="bd-sz__v${vv && vv.ok ? "" : " is-out"}">${txt}</span></div>`;
      }).join("");
    }
    paintQty();
  }

  $$(".bd-sw__b").forEach((p) => p.addEventListener("click", () => {
    state.color = p.dataset.color;
    on($$(".bd-sw__b"), p);
    const v = variantFor(state.color, null);
    if (v && v.media != null) showShot(v.media);
    sync();
  }));

  // Prefer Black as the resting colour when present; else the template's is-on swatch,
  // else the first variant's colour. Keeps collection/PDP defaults aligned even if an
  // option value order drifts.
  const swatches = $$(".bd-sw__b");
  const blackSwatch = swatches.find((p) => {
    const c = (p.dataset.color || "").trim().toLowerCase();
    return c === "black" || c === "blk";
  });
  const marked = $(".bd-sw__b.is-on");
  const pick = blackSwatch || marked || null;
  state.color = pick?.dataset.color ?? variants[0]?.c ?? null;
  if (pick) on(swatches, pick);
  {
    const v = variantFor(state.color, null);
    if (v && v.media != null) showShot(v.media);
  }

  $("[data-quote-add]")?.addEventListener("click", () => {
    const sizes = {};
    rows.forEach((r) => { if (state.qty[r.dataset.sizeRow] > 0) sizes[r.dataset.sizeRow] = state.qty[r.dataset.sizeRow]; });
    const total = totalQty();
    if (total === 0) return;

    const list = loadList();
    const thumb = $(".bd-thumb.is-on img") || $(".bd-view__img.is-on");
    const sizeText = Object.entries(sizes).map(([s, q]) => `${q} × ${s}`).join(", ");
    list.push({
      handle: PRODUCT.handle, title: PRODUCT.title, colour: state.color,
      delivery: DELIVERY_LABEL[state.delivery] || state.delivery,
      image: thumb?.src || "",
      sizes, sizeText, total,
    });
    saveList(list);

    // Reset the panel back to zero and show the same confirmation the handoff shows.
    rows.forEach((r) => { state.qty[r.dataset.sizeRow] = 0; });
    paintQty();
    const success = $("[data-qsuccess]");
    const line = $("[data-qsuccess-line]");
    if (success && line) {
      line.textContent = `Added ${total} piece${total === 1 ? "" : "s"} to your list.`;
      success.hidden = false;
      setTimeout(() => { success.hidden = true; }, 4000);
    }
  });

  /* accordion — single-open, per the handoff (openPanel state, only one open at a time). */
  const accBtns = $$("[data-acc-btn]"), accPanels = $$("[data-acc-panel]");
  function openAcc(name) {
    const btn = accBtns.find((b) => b.dataset.acc === name);
    const panel = accPanels.find((p) => p.dataset.acc === name);
    if (!btn || !panel) return null;
    const wasOpen = panel.classList.contains("is-on");
    accBtns.forEach((b) => { b.classList.remove("is-on"); b.setAttribute("aria-expanded", "false"); b.querySelector(".bd-acc__sign").textContent = "+"; });
    accPanels.forEach((p) => p.classList.remove("is-on"));
    if (!wasOpen) {
      btn.classList.add("is-on");
      btn.setAttribute("aria-expanded", "true");
      btn.querySelector(".bd-acc__sign").textContent = "−";
      panel.classList.add("is-on");
    }
    return panel;
  }
  accBtns.forEach((b) => b.addEventListener("click", () => openAcc(b.dataset.acc)));

  /* "You might also like" — horizontal scroll. Nav buttons only ever show when there is
     somewhere to go: prev stays hidden at the start of the row, next hides once the row is
     scrolled to its end, and the whole pair is absent from the DOM when 4 or fewer siblings
     exist (see the Liquid). */
  const relTrack = $("[data-rel-track]");
  const relPrev = $('[data-rel-nav="-1"]');
  const relNext = $('[data-rel-nav="1"]');
  if (relTrack && (relPrev || relNext)) {
    const EPS = 4;
    const updateRelNav = () => {
      const max = relTrack.scrollWidth - relTrack.clientWidth;
      if (relPrev) relPrev.hidden = relTrack.scrollLeft <= EPS;
      if (relNext) relNext.hidden = max <= EPS || relTrack.scrollLeft >= max - EPS;
    };
    const scrollRel = (dir) => {
      const card = relTrack.querySelector(".bd-rel__c");
      const step = card ? card.getBoundingClientRect().width + 24 : relTrack.clientWidth;
      relTrack.scrollBy({ left: step * dir, behavior: "smooth" });
    };
    relPrev?.addEventListener("click", () => scrollRel(-1));
    relNext?.addEventListener("click", () => scrollRel(1));
    relTrack.addEventListener("scroll", updateRelNav, { passive: true });
    window.addEventListener("resize", updateRelNav);
    updateRelNav();
  }

  /* studio temporarily disabled — the button no longer opens the studio (no piobox:open
     dispatch). Clicking flips it and it stays on "Coming Soon". Original hand-off kept below. */
  const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  $$("[data-studio-soon]").forEach((b) => b.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (b.classList.contains("is-soon")) return;
    const label = b.querySelector("[data-studio-label]");
    const swap = () => { if (label) label.textContent = "Coming Soon"; b.classList.add("is-soon"); };
    if (reduceMotion) { swap(); return; }
    b.classList.remove("is-flipping"); void b.offsetWidth;
    b.classList.add("is-flipping");
    setTimeout(swap, 300);
    b.addEventListener("animationend", () => b.classList.remove("is-flipping"), { once: true });
  }));
  /* ORIGINAL (disabled):
  /* studio hand-off — colour, size(s) and quantity carry across; the studio picks its own
     default decoration method now that there's no method picker on this page. * /
  $$("[data-studio-open]").forEach((b) => b.addEventListener("click", () => {
    const sizes = {};
    rows.forEach((r) => { if (state.qty[r.dataset.sizeRow] > 0) sizes[r.dataset.sizeRow] = state.qty[r.dataset.sizeRow]; });
    document.dispatchEvent(new CustomEvent("piobox:open", {
      detail: {
        color: state.color,
        quantity: totalQty() || 1,
        sizes: Object.keys(sizes).length ? sizes : null,
      },
    }));
  }));

  */

  sync();
})();
